import { combineReducers, Reducer } from 'redux';
import { persistReducer } from 'redux-persist';
import { Storage } from 'redux-persist/es/types';

import constants from '../Config/constants';

import { baseApi } from './api/baseApi';
import cartReducer from './cart';
import checkoutReducer from './checkout';
import errorReducer from './error';
import favouriteReducer from './favourite';
import filterReducer from './filter';
import mealPlanReducer from './mealPlan';
import noSessionReducer from './noSession';
import productReducer from './product';
import searchReducer from './search';
import shopReducer from './shop';
import shopPersistReducer from './shopPersist';
import userReducer from './user';
import utilityReducer from './utility';

const persistedKeys = {
  // used on web and mobile
  CART: 'cart',
  CHECKOUT: 'checkout',
  FAVORITE: 'favorite',
  NOSESSION: 'noSession', // dont remove
  SEARCH: 'search',
  SHOPPERSIST: 'shopPersist',
  USER: 'user',
  UTILITY: 'utility',
  // web persist but not on mobile
  FILTER: 'filter',
};

const persistedKeysToClear = Object.keys(persistedKeys)
  .map((key) => persistedKeys[key]) // get the keys value
  .filter((val) => val !== persistedKeys.NOSESSION); // exclude noSession

export default (storage: Storage, onClear?: Function) => {
  const createPersistReducer = (
    key: string,
    reducer: Reducer,
    options?: any,
  ) => {
    if (!reducer) {
      throw new Error(
        `❌ Tried to persistReducer for "${key}", but reducer is undefined`,
      );
    }
    if ((reducer as any)._persist) {
      console.warn(`[WARN] Skipping double wrap on reducer "${key}"`);
      return reducer;
    }
    const wrapped = persistReducer({ key, storage, ...options }, reducer);
    (wrapped as any)._persistWrapped = true;
    return wrapped;
  };

  const combinedReducers = combineReducers({
    [baseApi.reducerPath]: baseApi.reducer,
    // Persisted reducers
    cart: createPersistReducer(persistedKeys.CART, cartReducer, {
      blacklist: ['isAddingOrUpdatingCart'],
    }),
    checkout: createPersistReducer(persistedKeys.CHECKOUT, checkoutReducer, {
      blacklist: ['isPromoOk'],
    }),
    favourite: createPersistReducer(persistedKeys.FAVORITE, favouriteReducer),
    noSession: createPersistReducer(persistedKeys.NOSESSION, noSessionReducer),
    search: createPersistReducer(persistedKeys.SEARCH, searchReducer),
    shopPersist: createPersistReducer(
      persistedKeys.SHOPPERSIST,
      shopPersistReducer,
    ),
    user: createPersistReducer(persistedKeys.USER, userReducer),
    utility: createPersistReducer(persistedKeys.UTILITY, utilityReducer, {
      blacklist: [
        'gettingUserData',
        'hasOrderBanner',
        'isOrderBannerMinimized',
        'loadAllStoresAndActiveOrders',
        'orderDetails',
        'rescheduleStoreId',
        'route',
      ],
    }),
    // Non persisted reducers
    mealPlan: mealPlanReducer,
    product: productReducer,
    shop: shopReducer,
    // condition reducer for web and mobile
    ...(constants.isWeb
      ? {
          // Web persisted reducers
          filter: createPersistReducer(persistedKeys.FILTER, filterReducer, {
            blacklist: ['reservationStore'],
          }),
          // Web non persisted reducers
          error: errorReducer,
        }
      : {
          // Mobile non persisted reducers
          filter: filterReducer,
        }),
  });

  return (state: any, action: any) => {
    if (
      action?.meta?.arg?.endpointName === 'logout' &&
      action?.meta?.requestStatus === 'fulfilled'
    ) {
      onClear?.(); // call onClear if parameter is passed
      // clear persisted data
      persistedKeysToClear.forEach((key) =>
        storage.removeItem(`persist:${key}`),
      );
      // we return the noSession object instead of undefined
      // because noSession contain state of app behaviour e.g: remember login credential etc.
      return combinedReducers(
        { noSession: state.noSession } as ReturnType<typeof combinedReducers>,
        action,
      );
    }
    return combinedReducers(state, action);
  };
};
